import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { Link, LoadingOverlay, TextField } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { TextEditor } from "@src/components/ui-kit/TextEditor/TextEditor";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import type { FC } from "react";

export const SendQuoteModal: FC = observer(function SendQuoteModal() {
  const { sendQuoteModalStore: store } = useStore();

  return (
    <Modal
      isOpen
      onClose={store.modalState.onClose}
      scrollBehavior="inside"
      trapFocus={false}
    >
      <ModalOverlay />
      <ModalContent
        minW={{
          base: "full",
          md: "1000px",
        }}
      >
        <ModalHeader>
          <Trans>Send mail</Trans>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <LoadingOverlay isLoading={store.isLoading}>
            <VStack align="flex-start" spacing="4">
              <TextField
                label={t`Recipient (with approval right)`}
                value={store.form.$.primary_recipient.value}
                onChange={store.form.$.primary_recipient.onChange}
                error={store.form.$.primary_recipient.error}
              />
              <FormControl isInvalid={store.form.$.recipients.hasError}>
                <FormLabel>
                  <Trans>Recipients (CC)</Trans>
                </FormLabel>
                <Textarea
                  minH="33px"
                  resize="vertical"
                  onChange={({ target }) => {
                    store.form.$.recipients.onChange(target.value);
                  }}
                  value={store.form.$.recipients.value}
                />
                <HStack align="center" mt="2">
                  <Icon name="info-circle" />
                  <FormHelperText mt="0">
                    <Trans>
                      If you use multiple emails, please separate them with ", "
                    </Trans>
                  </FormHelperText>
                </HStack>
                <FormErrorMessage>
                  {store.form.$.recipients.error}
                </FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={store.form.$.subject.hasError}>
                <FormLabel>
                  <Trans>Subject</Trans>
                </FormLabel>
                <Input
                  onChange={({ target }) => {
                    store.form.$.subject.onChange(target.value);
                  }}
                  value={store.form.$.subject.value}
                />
                <FormErrorMessage>
                  {store.form.$.subject.error}
                </FormErrorMessage>
              </FormControl>
              <FormControl>
                <FormLabel>
                  <Trans>Content</Trans>
                </FormLabel>
                <TextEditor
                  ref={store.contentEditorRef}
                  minH="250px"
                  attachments={[]}
                  isEditable
                  initialValue={store.form.$.content.value}
                  onChange={(val) => {
                    store.form.$.content.onChange(val?.toString() ?? "");
                  }}
                  borderWidth="thin"
                  px="3"
                  borderColor="grey.200"
                  borderRadius="base"
                  transition="border-color 150ms ease-in"
                  _focusWithin={{
                    borderColor: "purple.500",
                  }}
                />
              </FormControl>
              {store.attachments.length > 0 && (
                <>
                  <Text>
                    <strong>
                      <Trans>Attachments</Trans>
                    </strong>
                  </Text>
                  {store.attachments.map((attachment) => (
                    <Link
                      key={`quote-attachment-${attachment.name}-${attachment.link}`}
                      href={attachment.link}
                      isExternal
                      target="_blank"
                    >
                      {attachment.name}
                    </Link>
                  ))}
                </>
              )}
            </VStack>
          </LoadingOverlay>
        </ModalBody>
        <ModalFooter>
          <Button
            mr="2"
            colorScheme="grey"
            onClick={store.modalState.onClose}
            variant="outline"
          >
            <Trans>Close</Trans>
          </Button>
          <Button
            colorScheme="violet"
            isDisabled={store.form.hasError}
            isLoading={store.isSubmitting}
            onClick={store.sendEmail}
          >
            <Trans>Send</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});
